import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import "./CompanyNumberingSettings.css";

const API_URL = "/api";

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

const normalizeSequence = (sequence) => ({
  ...sequence,
  display_name: sequence.display_name || "",
  prefix: sequence.prefix || "",
  suffix: sequence.suffix || "",
  next_number: sequence.next_number ?? 1,
  padding: sequence.padding ?? 6,
  reset_annually: sequence.reset_annually ?? false,
  is_active: sequence.is_active ?? true,
  preview: "",
});

export default function CompanyNumberingSettings({
  readOnly = false,
}) {
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [sequences, setSequences] = useState([]);

  const loadSequences = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_URL}/settings/numbering/me`,
        { headers: getHeaders() }
      );

      const initialSequences = response.data.map(
        normalizeSequence
      );

      setSequences(initialSequences);

      const previews = await Promise.all(
        initialSequences.map(async (sequence) => {
          const previewResponse = await axios.get(
            `${API_URL}/settings/numbering/me/${sequence.id}/preview`,
            { headers: getHeaders() }
          );

          return {
            id: sequence.id,
            preview: previewResponse.data.next_document_number,
          };
        })
      );

      setSequences((previous) =>
        previous.map((sequence) => {
          const preview = previews.find(
            (item) => item.id === sequence.id
          );

          return {
            ...sequence,
            preview: preview?.preview || "",
          };
        })
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
          "No fue posible cargar los consecutivos."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSequences();
  }, [loadSequences]);

  const handleChange = (id, event) => {
    const { name, value, type, checked } = event.target;

    setSequences((previous) =>
      previous.map((sequence) =>
        sequence.id === id
          ? {
              ...sequence,
              [name]: type === "checkbox" ? checked : value,
            }
          : sequence
      )
    );
  };

  const saveSequence = async (sequence) => {
    if (readOnly) return;

    try {
      setSavingId(sequence.id);

      const payload = {
        display_name: sequence.display_name.trim(),
        prefix: sequence.prefix.trim().toUpperCase(),
        suffix: sequence.suffix.trim().toUpperCase(),
        next_number: Number(sequence.next_number || 1),
        padding: Number(sequence.padding || 6),
        reset_annually: sequence.reset_annually,
        is_active: sequence.is_active,
      };

      const response = await axios.put(
        `${API_URL}/settings/numbering/me/${sequence.id}`,
        payload,
        { headers: getHeaders() }
      );

      const previewResponse = await axios.get(
        `${API_URL}/settings/numbering/me/${sequence.id}/preview`,
        { headers: getHeaders() }
      );

      setSequences((previous) =>
        previous.map((item) =>
          item.id === sequence.id
            ? {
                ...normalizeSequence(response.data),
                preview:
                  previewResponse.data.next_document_number,
              }
            : item
        )
      );

      toast.success(
        `Consecutivo de ${response.data.display_name} actualizado.`
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
          "No fue posible guardar el consecutivo."
      );
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        Cargando consecutivos...
      </div>
    );
  }

  return (
    <div className="numbering-settings">
      <div className="numbering-settings-heading">
        <div>
          <h3>Consecutivos del sistema</h3>
          <p>
            Define el formato y próximo número de cada
            documento generado por la empresa.
          </p>
        </div>
      </div>

      <div className="numbering-sequences-list">
        {sequences.map((sequence) => (
          <article
            className="numbering-sequence-card"
            key={sequence.id}
          >
            <div className="numbering-card-header">
              <div>
                <span className="numbering-document-type">
                  {sequence.document_type}
                </span>
                <h4>
                  {sequence.display_name || "Documento"}
                </h4>
              </div>
            </div>

            <div className="numbering-preview">
              <span>Próximo documento</span>
              <strong>
                {sequence.preview || "Calculando..."}
              </strong>
            </div>

            <fieldset
              className="numbering-settings-fieldset"
              disabled={readOnly}
            >
              <label
                className={
                  sequence.is_active
                    ? "numbering-active active"
                    : "numbering-active inactive"
                }
              >
                <input
                  type="checkbox"
                  name="is_active"
                  checked={sequence.is_active}
                  onChange={(event) =>
                    handleChange(sequence.id, event)
                  }
                />
                {sequence.is_active ? "Activo" : "Inactivo"}
              </label>

              <div className="numbering-settings-grid">
                <div className="floating-field">
                  <input
                    id={`display-name-${sequence.id}`}
                    name="display_name"
                    value={sequence.display_name}
                    onChange={(event) =>
                      handleChange(sequence.id, event)
                    }
                    placeholder=" "
                    maxLength={100}
                  />
                  <label htmlFor={`display-name-${sequence.id}`}>
                    Nombre del documento
                  </label>
                </div>

                <div className="floating-field">
                  <input
                    id={`prefix-${sequence.id}`}
                    name="prefix"
                    value={sequence.prefix}
                    onChange={(event) =>
                      handleChange(sequence.id, event)
                    }
                    placeholder=" "
                    maxLength={20}
                  />
                  <label htmlFor={`prefix-${sequence.id}`}>
                    Prefijo
                  </label>
                </div>

                <div className="floating-field">
                  <input
                    id={`next-number-${sequence.id}`}
                    name="next_number"
                    type="number"
                    min="1"
                    value={sequence.next_number}
                    onChange={(event) =>
                      handleChange(sequence.id, event)
                    }
                    placeholder=" "
                  />
                  <label htmlFor={`next-number-${sequence.id}`}>
                    Próximo número
                  </label>
                </div>

                <div className="floating-field">
                  <input
                    id={`padding-${sequence.id}`}
                    name="padding"
                    type="number"
                    min="1"
                    max="12"
                    value={sequence.padding}
                    onChange={(event) =>
                      handleChange(sequence.id, event)
                    }
                    placeholder=" "
                  />
                  <label htmlFor={`padding-${sequence.id}`}>
                    Cantidad de dígitos
                  </label>
                </div>

                <div className="floating-field">
                  <input
                    id={`suffix-${sequence.id}`}
                    name="suffix"
                    value={sequence.suffix}
                    onChange={(event) =>
                      handleChange(sequence.id, event)
                    }
                    placeholder=" "
                    maxLength={20}
                  />
                  <label htmlFor={`suffix-${sequence.id}`}>
                    Sufijo
                  </label>
                </div>
              </div>

              <div className="numbering-options">
                <label className="numbering-option">
                  <input
                    type="checkbox"
                    name="reset_annually"
                    checked={sequence.reset_annually}
                    onChange={(event) =>
                      handleChange(sequence.id, event)
                    }
                  />
                  Reiniciar consecutivo cada año
                </label>
              </div>
            </fieldset>

            <div className="numbering-actions">
              {!readOnly ? (
                <button
                  type="button"
                  onClick={() => saveSequence(sequence)}
                  disabled={savingId === sequence.id}
                >
                  {savingId === sequence.id
                    ? "Guardando..."
                    : "Guardar consecutivo"}
                </button>
              ) : (
                <span className="settings-view-only">
                  Solo consulta
                </span>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}